import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { gsrReportApi } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import Button from '../components/common/Button'

/**
 * PastReports — read-only history of GSR + Area reports submitted by the
 * current user. Reached via the "Past Reports" button on /reports.
 *
 * Each card shows summary info plus Preview / Download / Show details
 * buttons. Details inline-expand to show comments + celebrations without
 * leaving the page.
 */
export default function PastReports() {
    const { user } = useAuth()
    const hasReporting = user?.isGsr === true || user?.isAreaAdmin === true

    const [reports, setReports] = useState([])
    const [loading, setLoading] = useState(true)
    const [expandedId, setExpandedId] = useState(null)

    useEffect(() => {
        if (hasReporting) load()
        else setLoading(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasReporting])

    async function load() {
        setLoading(true)
        try {
            const response = await gsrReportApi.list()
            if (response.success) setReports(response.reports)
            else toast.error(response.message || 'Could not load reports')
        } catch {
            toast.error('Network error loading reports')
        } finally {
            setLoading(false)
        }
    }

    async function handlePreview(id, e) {
        e.stopPropagation()
        try {
            const { blob } = await gsrReportApi.fetchPdf(id, { inline: true })
            const url = URL.createObjectURL(blob)
            window.open(url, '_blank')
            setTimeout(() => URL.revokeObjectURL(url), 60_000)
        } catch {
            toast.error('Could not load PDF')
        }
    }

    async function handleDownload(id, e) {
        e.stopPropagation()
        try {
            const { blob, fileName } = await gsrReportApi.fetchPdf(id, { inline: false })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = fileName
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
        } catch {
            toast.error('Could not download PDF')
        }
    }

    if (!hasReporting) {
        return (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center max-w-xl mx-auto">
                <h2 className="text-xl font-semibold text-ghasc-navy">No reports for your role</h2>
                <p className="text-gray-600 mt-2 text-sm">
                    Monthly reports are submitted by GSRs and area officers. If you should have access,
                    talk to the literature chair.
                </p>
                <div className="mt-4">
                    <Link to="/"><Button>Back to Dashboard</Button></Link>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h2 className="text-2xl font-semibold text-ghasc-navy">Past Reports</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Reports you've submitted. View-only — submit a new one for corrections.
                    </p>
                </div>
                <Link to="/reports">
                    <Button variant="secondary">Submit a new report</Button>
                </Link>
            </div>

            {loading ? (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">Loading...</div>
            ) : reports.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">
                    No reports yet. Click <strong>Submit a new report</strong> to file your first one.
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {reports.map(r => {
                        const expanded = expandedId === r.id
                        const isGsr = !r.position
                        const submitterDisplay = r.submitterName || r.userName || '—'

                        return (
                            <div key={r.id} className="bg-white rounded-xl shadow-sm p-4 flex flex-col gap-3">
                                {/* Top: type + month */}
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <div className="text-ghasc-navy font-semibold">
                                            {isGsr ? 'GSR Report' : 'Area Report'}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-0.5">
                                            For {r.forTheMonth}
                                        </div>
                                    </div>
                                    {!isGsr && (
                                        <span className="flex-shrink-0 inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            {r.position}
                                        </span>
                                    )}
                                </div>

                                {/* Submitter */}
                                <div>
                                    <div className="text-sm text-gray-900">{submitterDisplay}</div>
                                    <div className="text-xs text-gray-500">
                                        Submitted {new Date(r.createdAt).toLocaleDateString('en-US', {
                                            month: 'short', day: 'numeric', year: 'numeric',
                                        })}
                                    </div>
                                </div>

                                {/* Stats row */}
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    {/* Budget Used — Area reports only */}
                                    {!isGsr && r.budgetAmount != null ? (
                                        <div>
                                            <div className="text-xs uppercase tracking-wider text-gray-500">Budget Used</div>
                                            <div className="font-medium">${Number(r.budgetAmount).toFixed(2)}</div>
                                        </div>
                                    ) : <div />}
                                    {/* Celebrations — GSR reports only */}
                                    {isGsr && r.celebrations && r.celebrations.length > 0 && (
                                        <div className="text-right">
                                            <div className="text-xs uppercase tracking-wider text-gray-500">Celebrations</div>
                                            <div className="font-medium">{r.celebrations.length}</div>
                                        </div>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                                    <button
                                        type="button"
                                        onClick={(e) => handlePreview(r.id, e)}
                                        className="flex-1 min-w-[90px] px-3 py-2 text-sm text-ghasc-navy bg-gray-50 rounded-lg hover:bg-gray-100"
                                    >
                                        Preview
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(e) => handleDownload(r.id, e)}
                                        className="flex-1 min-w-[90px] px-3 py-2 text-sm text-ghasc-navy bg-gray-50 rounded-lg hover:bg-gray-100"
                                    >
                                        Download
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setExpandedId(expanded ? null : r.id)}
                                        className="flex-1 min-w-[90px] px-3 py-2 text-sm text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100"
                                    >
                                        {expanded ? 'Hide' : 'Details'}
                                    </button>
                                </div>

                                {/* Expanded details */}
                                {expanded && (
                                    <div className="border-t border-gray-100 pt-3 text-sm space-y-2">
                                        <div>
                                            <span className="text-xs text-gray-500">Report Date: </span>
                                            <span className="text-gray-700">{new Date(r.reportDate).toLocaleDateString()}</span>
                                        </div>
                                        {r.comments && (
                                            <div>
                                                <div className="text-xs text-gray-500 mb-0.5">Comments</div>
                                                <p className="text-gray-700 whitespace-pre-wrap">{r.comments}</p>
                                            </div>
                                        )}
                                        {isGsr && r.celebrations && r.celebrations.length > 0 && (
                                            <div>
                                                <div className="text-xs text-gray-500 mb-1">Celebrations</div>
                                                <ul className="space-y-1">
                                                    {r.celebrations.map(c => (
                                                        <li key={c.id} className="text-gray-700 text-xs">
                                                            <span className="font-medium">{c.name}</span> — {c.noOfYears} year{c.noOfYears === 1 ? '' : 's'}
                                                            <span className="text-gray-500"> on {new Date(c.celebrationDate).toLocaleDateString()}</span>
                                                            {!c.postPublicly && (
                                                                <span className="ml-2 text-xs text-amber-700">(do not post publicly)</span>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}